import { Alert, CircularProgress } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  CLOSE_REASON,
  type CloseHandler,
  type CloseReason,
  IGNORED_REASONS
} from '@/pages/Administration/Devices/Devices.types';
import useCustomNotification, {
  type CustomNotificationProps
} from '@/pages/Administration/Devices/SyncDevices/useCustomNotification';

import { useLogSourcesDialog } from '../LogSourcesCreate.hooks';
import {
  editedLogSourcesAtom,
  logSourcesAtom,
  savingInProgressAtom
} from '../LogSourcesCreate.store';
import type {
  DataStreamModel,
  LogSourceConfigDTO,
  LogSourceModel
} from '../LogSourcesCreate.types';
import { mergeSources } from '../LogSourcesCreate.utils';
import LogSourcesCreateForm from '../LogSourcesCreateForm';
import useStyles from './LogSourcesCreateDialog.styles';

const LogSourcesCreateDialog = () => {
  const formMethods = useForm();
  const { handleSubmit, reset, setError, clearErrors, setValue } = formMethods;
  const [logSources] = useAtom(logSourcesAtom);
  const [savingInProgress] = useAtom(savingInProgressAtom);
  const {
    open: showLogSourcesPopup,
    error,
    mutation,
    mode,
    clearAllLogSourcesSelections,
    closeLogSourcesDialog,
    clearApiError
  } = useLogSourcesDialog();
  const [customNotifyProps, setCustomNotifyProps] =
    useState<CustomNotificationProps>({
      title: '',
      message: '',
      severity: 'info',
      buttonType: 'Close'
    });
  const {
    ConfirmationModal: CustomNotificationModal,
    confirm: ShowCustomNotification
  } = useCustomNotification(customNotifyProps);

  const { classes } = useStyles({ mode });

  const [scroll] = useState<DialogProps['scroll']>('paper');
  const [editedLogSources] = useAtom(editedLogSourcesAtom);

  const resetAndClose = () => {
    clearAllLogSourcesSelections();
    closeLogSourcesDialog();
    clearErrors();
    clearApiError();
    // reset form values
    reset();
  };

  const handleClose: CloseHandler = async (
    _event: object,
    reason: CloseReason
  ) => {
    if (IGNORED_REASONS.includes(reason)) {
      return;
    }
    resetAndClose();
  };

  const parseJson = (jsonString: string | undefined) => {
    try {
      if (jsonString) {
        const parsed = JSON.parse(jsonString);
        return parsed;
      }
      return null;
    } catch (_e) {
      return null;
    }
  };

  const title = useMemo(() => {
    let titleStr = '';
    if (mode === 'AddLogSources') {
      titleStr = 'Add Log Sources';
    }

    if (mode === 'EditLogSources') {
      titleStr = `Edit Log Sources`;
    }

    return titleStr;
  }, [mode]);

  const generatePayloadFromSelectedItems = (
    selectedItems: LogSourceModel[],
    configurations: Record<string, string>
  ) => {
    const result = {
      sources: [] as string[],
      source_configs: {} as { [key: string]: object },
      input_configs: {} as { [key: string]: object }
    };
    const sourceSet = new Set<string>(); // prevent duplicates

    selectedItems.forEach((item) => {
      if (item.isSelected) {
        // Check if all streams are selected
        if (
          item.data_streams?.every(
            (stream: DataStreamModel) => stream.isSelected
          )
        ) {
          if (item?.data_streams?.length > 0) {
            // Add product name ending with "*"
            sourceSet.add(`${item.name}*`);
          } else {
            sourceSet.add(item.name);
          }
        } else if (item.data_streams) {
          // Add individual streams if not all are selected
          item.data_streams.forEach((stream: DataStreamModel) => {
            if (stream.isSelected) {
              sourceSet.add(stream.name);
            }
          });
        } else {
          // Add the product name directly if no streams exist
          sourceSet.add(item.name);
        }
      } else if (item.data_streams) {
        item.data_streams
          .filter((stream: DataStreamModel) => stream.isSelected)
          .forEach((stream) => {
            sourceSet.add(stream.name);
          });
      }

      // Check for specific configuration values for this product and
      // clearing the configuration values for the products which are not selected
      const allStreamsSelected = item?.isSelected;
      const anySelected =
        item?.data_streams?.some(
          (stream: DataStreamModel) => stream.isSelected
        ) ?? false;

      const configKey = `${item.name}_configuration`;

      if (anySelected || allStreamsSelected) {
        const configValue = configurations?.[configKey];
        if (configValue) {
          const configJson = parseJson(configValue);
          if (configJson?.timezone?.trim()) {
            result.source_configs[item.name] = configJson;
          }
        }
      } else {
        setValue(configKey, '');
      }
    });

    result.sources = Array.from(sourceSet);

    // Input configurations
    const inputConfigKey = `input_configuration`;
    if (configurations[inputConfigKey]) {
      const configJson = parseJson(configurations[inputConfigKey]);
      result.input_configs = configJson || {};
    }

    return result;
  };

  const handleLogSourcesSubmit = async (data: Record<string, string>) => {
    if (mode === 'AddLogSources' || mode === 'EditLogSources') {
      clearApiError();
      // Validate JSON configuration for each product
      let hasInvalidJson = false;
      logSources?.forEach((item) => {
        const anyStreamSelected = item?.data_streams?.some(
          (stream: DataStreamModel) => stream.isSelected
        );
        if (item.isSelected || anyStreamSelected) {
          // Check for specific configuration values for this product
          const configKey = `${item.name}_configuration`;
          if (data[configKey]) {
            const configJson = parseJson(data[configKey]);
            if (configJson) {
              clearErrors(configKey);
            } else {
              setError(configKey, {
                type: 'manual',
                message: 'Invalid JSON format'
              });
              hasInvalidJson = true;
            }
          }
        }
      });

      // Check for input configuration JSON
      const configKey = `input_configuration`;
      if (data[configKey]) {
        const configJson = parseJson(data[configKey]);
        if (configJson) {
          clearErrors(configKey);
        } else {
          setError(configKey, {
            type: 'manual',
            message: 'Invalid JSON format'
          });
          hasInvalidJson = true;
        }
      }

      if (hasInvalidJson) {
        setCustomNotifyProps({
          title: 'Log Sources',
          message: 'Invalid Json Format',
          severity: 'info',
          buttonType: 'Close'
        });
        ShowCustomNotification();
        return;
      }

      // Filter selected products and data streams
      const selectedData = logSources.filter(
        (item) =>
          item?.isSelected ||
          item?.data_streams?.some(
            (stream: DataStreamModel) => stream.isSelected
          )
      );

      // When adding log sources, at least one log source should be selected
      if (mode === 'AddLogSources' && selectedData.length === 0) {
        setCustomNotifyProps({
          title: 'Log Sources',
          message: 'No Log Sources Selected',
          severity: 'info',
          buttonType: 'Close'
        });
        ShowCustomNotification();
      }

      // Instead of deleting the log sources, we will just clear the selections
      if (mode === 'EditLogSources' && selectedData.length === 0) {
        setCustomNotifyProps({
          title: 'Log Sources',
          message:
            'No log sources have been selected. Are you sure you want to proceed with saving?.',
          severity: 'warning',
          buttonType: 'YesNo'
        });
        const confirmed = await ShowCustomNotification();
        if (confirmed) {
          const payload = {
            sources: [] as string[],
            source_configs: {} as { [key: string]: object },
            input_configs: {} as { [key: string]: object }
          };
          mutation.mutate(payload as LogSourceConfigDTO);
        }
      }

      // Proceed with saving the selected log sources
      if (selectedData.length > 0) {
        setCustomNotifyProps({
          title: 'Log Sources',
          message:
            "Are you sure you want to save the selected log sources? These changes are activated automatically and don't need to be synced",
          severity: 'warning',
          buttonType: 'YesNo'
        });
        const confirmed = await ShowCustomNotification();
        if (confirmed) {
          const newPayload = generatePayloadFromSelectedItems(logSources, data);
          let finalPayload = newPayload;

          if (mode === 'EditLogSources' && editedLogSources?.sources) {
            const existingSources = editedLogSources.sources as string[];

            const mergedSources = mergeSources(
              existingSources,
              newPayload.sources,
              logSources
            );

            finalPayload = {
              ...newPayload,
              sources: mergedSources,
              source_configs: newPayload.source_configs
            };
          }

          if (finalPayload.sources.length > 0) {
            mutation.mutate(finalPayload as LogSourceConfigDTO);
          }
        }
      }
    }
  };
  const handleError = () => {
    setCustomNotifyProps({
      title: 'Log Sources',
      message: 'Invalid Json Format',
      severity: 'info',
      buttonType: 'Close'
    });
    ShowCustomNotification();
  };

  const buttonLabel = useMemo(() => {
    if (mode === 'EditLogSources') return 'Update';
    if (mode === 'AddLogSources') return 'Create';
    return 'Submit';
  }, [mode]);

  // Disable save button conditions
  const isSaveDisabled = savingInProgress || mutation.isPending;

  return (
    <Dialog
      className={classes.dialogContainer}
      open={showLogSourcesPopup}
      onClose={handleClose}
      scroll={scroll}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      fullWidth
    >
      <DialogTitle id="scroll-dialog-title">{title}</DialogTitle>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(handleLogSourcesSubmit, handleError)}>
          <DialogContent>
            {(mode === 'AddLogSources' || mode === 'EditLogSources') && (
              <LogSourcesCreateForm />
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={(e: React.MouseEvent<HTMLElement>) =>
                handleClose(e, CLOSE_REASON.BUTTON)
              }
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="primary"
              size="small"
              type="submit"
              disabled={isSaveDisabled}
            >
              {!mutation.isPending && <span>{buttonLabel}</span>}
              {mutation.isPending && (
                <CircularProgress color="secondary" size={20} />
              )}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
      <CustomNotificationModal />
    </Dialog>
  );
};
export default LogSourcesCreateDialog;
